import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Customers.css';
import SideBar from '../SideBar/SideBar';
import AuthService from '../../services/auth.service';
import customersService from '../../services/customers.service';

interface Note {
  id: number;
  text: string;
  timestamp: string;
}

interface Customer {
  id: number;
  username: string;
  email: string;
  role: string;
  notes: Note[];
  orders: OrderHistory[];
  appointments: AppointmentHistory[];
}

interface OrderHistory {
  id: number;
  date: string;
  total: number;
  status: string;
  customerId: number;
}

interface AppointmentHistory {
  id: number;
  date: string;
  service: string;
  status: string;
  customerId: number;
}

const Customers: React.FC = () => {
  const location = useLocation();
  // Placeholder data - αργότερα θα έρχονται από το backend
  const [customers, setCustomers] = useState<Customer[]>([]);

  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [errorCustomers, setErrorCustomers] = useState<string | null>(null);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');

  // New State for Employee Management
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState({ username: '', email: '', password: '' });
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (user) {
        setIsAdmin(user.roles && user.roles.includes('ROLE_ADMIN'));
    }
  }, []);

  useEffect(() => {
    setLoadingCustomers(true);
    customersService.getCustomers()
      .then((data) => {
        setCustomers(data || []);
        setErrorCustomers(null);
      })
      .catch(() => {
        setErrorCustomers('Failed to load customers.');
      })
      .finally(() => setLoadingCustomers(false));
  }, []);


  const handleHistoryClick = (customer: Customer) => {
      setSelectedCustomer(customer);
      setShowHistoryModal(true);
  };

  const handleNotesClick = (customer: Customer) => {
      setSelectedCustomer(customer);
      setNewNoteText('');
      setShowNotesModal(true);
  };

  const handleAddNote = () => {
      if (selectedCustomer && newNoteText.trim()) {
          const newNote: Note = {
              id: Date.now(),
              text: newNoteText,
              timestamp: new Date().toLocaleString('el-GR')
          };

          const updatedCustomer = {
              ...selectedCustomer,
              notes: [...selectedCustomer.notes, newNote]
          };

          setCustomers(customers.map(c => 
              c.id === selectedCustomer.id ? updatedCustomer : c
          ));
          
          setSelectedCustomer(updatedCustomer); 
          setNewNoteText('');
      }
  };

  const handleAddEmployee = () => {
    if (newEmployee.username && newEmployee.email) {
      const newEmp: Customer = {
        id: Date.now(),
        username: newEmployee.username,
        email: newEmployee.email, // In real app, this should be handled by backend
        role: 'Employee',
        notes: [],
        orders: [],
        appointments: []
      };
      setCustomers([...customers, newEmp]);
      setShowAddEmployeeModal(false);
      setNewEmployee({ username: '', email: '', password: '' });
    }
  };

  const handleDelete = (id: number) => {
      if(window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτόν τον Employee;')) {
        setCustomers(customers.filter(c => c.id !== id));
      }
  };

  const getCustomerOrders = (customer: Customer | null) => customer?.orders || [];
  const getCustomerAppointments = (customer: Customer | null) => customer?.appointments || [];

  const query = new URLSearchParams(location.search);
  const roleFilter = query.get("role");

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          customer.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesRole = true;
    if (roleFilter) {
        matchesRole = customer.role.toLowerCase() === roleFilter.toLowerCase();
    }

    return matchesSearch && matchesRole;
  });

  return (
    <div className="admin-container">
      <SideBar />
      <div className="admin-content">
        <div className="content-header">
            <h2>
              {roleFilter === 'employee' ? 'Διαχείριση Υπαλλήλων' : 
               roleFilter === 'admin' ? 'Διαχείριση Διαχειριστών' : 
               roleFilter === 'customer' ? 'Διαχείριση Πελατών' : 
               'Διαχείριση Χρηστών'}
            </h2>
            
            <div className="search-container">
                <input 
                    type="text" 
                    className="search-input" 
                    placeholder="Αναζήτηση με όνομα ή email..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </div>

        </div>
        
        <div className="table-container">
            <table className="customers-table">
            <thead>
                <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Ενέργειες</th>
                </tr>
            </thead>
            <tbody>
                {loadingCustomers ? (
                    <tr>
                        <td colSpan={5} style={{textAlign: 'center', padding: '20px'}}>
                            Loading...
                        </td>
                    </tr>
                ) : errorCustomers ? (
                    <tr>
                        <td colSpan={5} style={{textAlign: 'center', padding: '20px'}}>
                            {errorCustomers}
                        </td>
                    </tr>
                ) : (
                    filteredCustomers.map((customer) => (
                    <tr key={customer.id}>
                        <td>#{customer.id}</td>
                        <td><b>{customer.username}</b></td>
                        <td>{customer.email}</td>
                        <td>
                            <span className={`role-badge ${customer.role.toLowerCase()}`}>
                                {customer.role}
                            </span>
                        </td>
                        <td>
                        <button className="action-btn history" onClick={() => handleHistoryClick(customer)}>
                            History
                        </button>
                        {/* <button className="action-btn notes" onClick={() => handleNotesClick(customer)}>
                            Notes
                        </button> */}
                        
                        {customer.role === 'Employee' && isAdmin && (
                            <button className="action-btn delete" onClick={() => handleDelete(customer.id)}>
                                Delete
                            </button>
                        )}
                        </td>
                    </tr>
                    ))
                )}
                {!loadingCustomers && !errorCustomers && filteredCustomers.length === 0 && (
                    <tr>
                        <td colSpan={5} style={{textAlign: 'center', padding: '20px'}}>
                            No results found.
                        </td>
                    </tr>
                )}
            </tbody>
            </table>
        </div>

        {/* History Modal */}
        {showHistoryModal && selectedCustomer && (
            <div className="modal-overlay">
                <div className="modal-content history-modal">
                    <div className="modal-header">
                        <h3>Ιστορικό Πελάτη: {selectedCustomer.username}</h3>
                        <button className="close-btn" onClick={() => setShowHistoryModal(false)}>×</button>
                    </div>
                    
                    <div className="history-sections">
                        <div className="history-section">
                            <h4>Παραγγελίες</h4>
                            {getCustomerOrders(selectedCustomer).length > 0 ? (
                                <table className="history-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Ημερομηνία</th>
                                            <th>Ποσό</th>
                                            <th>Κατάσταση</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCustomerOrders(selectedCustomer).map(order => (
                                            <tr key={order.id}>
                                                <td>#{order.id}</td>
                                                <td>{order.date}</td>
                                                <td>{order.total}€</td>
                                                <td>{order.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : <p>Δεν υπάρχουν παραγγελίες.</p>}
                        </div>

                        <div className="history-section">
                            <h4>Ραντεβού</h4>
                             {getCustomerAppointments(selectedCustomer).length > 0 ? (
                                <table className="history-table">
                                    <thead>
                                        <tr>
                                            <th>Ημερομηνία</th>
                                            <th>Υπηρεσία</th>
                                            <th>Κατάσταση</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getCustomerAppointments(selectedCustomer).map(app => (
                                            <tr key={app.id}>
                                                <td>{app.date}</td>
                                                <td>{app.service}</td>
                                                <td>{app.status}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : <p>Δεν υπάρχουν ραντεβού.</p>}
                        </div>
                    </div>
                    
                    <div className="modal-actions">
                        <button className="cancel-btn" onClick={() => setShowHistoryModal(false)}>Κλείσιμο</button>
                    </div>
                </div>
            </div>
        )}

        {/* Notes Modal */}
        {showNotesModal && selectedCustomer && (
            <div className="modal-overlay">
                <div className="modal-content notes-modal">
                    <div className="modal-header">
                        <h3>Σημειώσεις για {selectedCustomer.username}</h3>
                        <button className="close-btn" onClick={() => setShowNotesModal(false)}>×</button>
                    </div>
                    
                    <div className="notes-list">
                        <h4>Ιστορικό Σημειώσεων</h4>
                        {selectedCustomer.notes.length > 0 ? (
                            selectedCustomer.notes.map(note => (
                                <div key={note.id} className="note-item">
                                    <div className="note-timestamp">{note.timestamp}</div>
                                    <div className="note-text">{note.text}</div>
                                </div>
                            ))
                        ) : (
                            <p className="no-notes">Δεν υπάρχουν σημειώσεις.</p>
                        )}
                    </div>

                    <div className="add-note-section">
                        <h4>Προσθήκη Νέας Σημείωσης</h4>
                        <textarea
                            className="admin-notes-textarea"
                            value={newNoteText}
                            onChange={(e) => setNewNoteText(e.target.value)}
                            placeholder="Γράψτε εδώ τη νέα σημείωση..."
                            rows={3}
                        />
                         <div className="modal-actions">
                            <button className="save-btn" onClick={handleAddNote}>Προσθήκη</button>
                        </div>
                    </div>
                   
                    <div className="modal-footer-actions">
                         <button className="cancel-btn" onClick={() => setShowNotesModal(false)}>Κλείσιμο</button>
                    </div>
                </div>
            </div>
        )}

        {/* Add Employee Modal */}
        {showAddEmployeeModal && (
            <div className="modal-overlay">
                <div className="modal-content">
                    <div className="modal-header">
                        <h3>Προσθήκη Employee</h3>
                        <button className="close-btn" onClick={() => setShowAddEmployeeModal(false)}>×</button>
                    </div>
                    <div className="modal-body">
                        <input 
                            type="text" 
                            placeholder="Username" 
                            value={newEmployee.username} 
                            onChange={(e) => setNewEmployee({...newEmployee, username: e.target.value})}
                            style={{display: 'block', width: '100%', padding: '8px', marginBottom: '10px'}}
                        />
                        <input 
                            type="email" 
                            placeholder="Email" 
                            value={newEmployee.email} 
                            onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                            style={{display: 'block', width: '100%', padding: '8px', marginBottom: '10px'}}
                        />
                         <input 
                            type="password" 
                            placeholder="Password" 
                            value={newEmployee.password} 
                            onChange={(e) => setNewEmployee({...newEmployee, password: e.target.value})}
                             style={{display: 'block', width: '100%', padding: '8px', marginBottom: '10px'}}
                        />
                    </div>
                    <div className="modal-actions">
                        <button className="save-btn" onClick={handleAddEmployee}>Προσθήκη</button>
                        <button className="cancel-btn" onClick={() => setShowAddEmployeeModal(false)}>Ακύρωση</button>
                    </div>
                </div>
            </div>
        )}

      </div>
    </div>
  );
};

export default Customers;
